"use client";

import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NuqsAdapter } from 'nuqs/adapters/next/app'

import LocaleProvider from '@/lib/context/LocaleContext'
import { WindowWidthProvider } from '@/lib/context/WindowWidthContext'
import { Locale } from '@/i18n/i18n-config';
import moment from 'moment';
import 'moment/locale/de'

interface ProvidersProps {
    children: React.ReactNode;
    locale: Locale;
}

function makeQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // With SSR, we usually want to set some default staleTime
                // above 0 to avoid refetching immediately on the client
                staleTime: 60 * 1000,
            },
        },
    })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
    if (typeof window === 'undefined') {
        // Server: always make a new query client
        return makeQueryClient()
    } else {
        // Browser: make a new query client if we don't already have one
        // This is very important so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        if (!browserQueryClient) {
            browserQueryClient = makeQueryClient()
        }

        return browserQueryClient
    }
}

export default function Providers({ children, locale }: ProvidersProps) {
    // NOTE: Avoid useState when initializing the query client if you don't
    //       have a suspense boundary between this and the code that may
    //       suspend because React will throw away the client on the initial
    //       render if it suspends and there is no boundary
    const queryClient = getQueryClient()

    moment.locale(locale)

    return (
        <NuqsAdapter>
            <GoogleReCaptchaProvider reCaptchaKey={process.env.RECAPTCHA_SITE_KEY || ''}>
                <WindowWidthProvider>
                    <QueryClientProvider client={queryClient}>
                        <LocaleProvider locale={locale || 'en'}>
                            {children}
                        </LocaleProvider>
                    </QueryClientProvider>
                </WindowWidthProvider>
            </GoogleReCaptchaProvider>
        </NuqsAdapter>
    );
}